import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Box, TextField, Typography } from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { AgGridReact } from "ag-grid-react";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNull } from "lodash";

import codeUtil from "@plannel/pages/utils/code-util";
import reduxUtil from "@plannel/utils/redux-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";

import FilterContainer from "@plannel/components/layout/FilterContainer";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import { SettingsButton, FilterButton } from "@plannel/components/ActionIconButton";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import BucketToggleButton from "@plannel/components/BucketToggleButton"
import Snackbar from "@plannel/components/Snackbar";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import debug from "@plannel/utils/debug-util";

import shippingAlertService from "@plannel/services/rp/rp-shipping-alert-service";
import p13nService from "@plannel/services/system/p13n-service";
import dateUtils from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

const { versionStatusCode, muicolor, buckets } = codeUtil;
const { PLANNED, RELEASED } = versionStatusCode;
const { snackBarMessageColor } = ColorUtil;

const PAGE_CD = "SHIPPING_ALERT_THRESHOLD";
const pageSizes = [30, 50, 100];
const INV_CLASS_CD_LIST = codeUtil.dpABCConfig
const CURRENT_DATE = new ZDate();

const ShippingAlert = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [gridLoading, setGridLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ?? null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { location: null, item: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ?? null);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? muicolor.INFO : muicolor.DEFAULT);
  const [openFilter, setOpenFilter] = useState(false);

  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);
  const [sortParams, setSortParams] = useState({});

  const [rowData, setRowData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);
  const [chartYAxisMin, setChartYAxisMin] = useState(0);
  const [chartLeftYAxisMax, setChartLeftYAxisMax] = useState("auto");
  const [chartRightYAxisDomain, setChartRightYAxisDomain] = useState([0, "auto"]);
  const [chartLeftYAxisTicks, setChartLeftYAxisTicks] = useState(undefined);
  const [chartRightYAxisTicks, setChartRightYAxisTicks] = useState(undefined);

  const [bucketType, setBucketType] = useState(rpVersionFilter?.bucket?.toLowerCase() ?? buckets.WEEK);
  const [threshold, setThreshold] = useState(reduxViewState?.threshold ?? { upper: null, lower: null });
  const thresholdRef = useRef(reduxViewState?.threshold ?? { upper: null, lower: null });
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [thresholdInput, setThresholdInput] = useState({ upper: "", lower: "" });
  const [selectedRow, setSelectedRow] = useState(null);

  const gridRef = useRef();
  const selectedRowRef = useRef(null);
  const chartRawRef = useRef(null); // 마지막 차트 응답 — 언어 변경 시 재조립용

  const defaultBarTypeOptions = { strokeWidth: 3, barSize: 30 };
  const defaultLineTypeOptions = { strokeWidth: 2 };
  const chartSeries = [
    { field: "invOnHand", text: t("onhandQty"), type: "bar", typeOptions: { ...defaultBarTypeOptions, stackId: "STACK" }, orientation: "left", groupName: t("qty") },
    { field: "shipActQty", text: t("shipmentQtyCfm"), type: "bar", typeOptions: { ...defaultBarTypeOptions, stackId: "STACK" }, orientation: "left", groupName: t("qty") },
    { field: "targetInvQty", text: t("targetInventory"), type: "bar", typeOptions: { ...defaultBarTypeOptions, stackId: "STACK" }, orientation: "left", groupName: t("qty") },
    { field: "shipPlanQty", text: t("shipmentQtyPlan"), type: "line", typeOptions: { ...defaultLineTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: "progress", text: t("progress"), type: "line", typeOptions: { ...defaultLineTypeOptions }, orientation: "right", groupName: "%", formatter: value => value == null || isNaN(value) ? "-" : `${Math.round(value)}%` },
  ];
  const pivotConfig = useMemo(() => ({
    groupKey: "measure",
    chartSeries,
    include: chartLabel,
    // chartSeries 가 t() 로 만들어지므로 t 를 의존성에 둔다(빠지면 언어를 바꿔도 옛 텍스트가 남는다).
  }), [chartLabel, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const thresholdLabel = useMemo(() => {
    const { upper, lower } = threshold;
    if (upper != null && lower != null) return `${lower}%↓ / ${upper}%↑`;
    if (upper != null) return `${upper}%↑`;
    if (lower != null) return `${lower}%↓`;
    return null;
  }, [threshold]);

  const columnDefs = [
    { headerName: "locCd", field: "destLocCd", colId: "locCd", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "locName", field: "destLocName", colId: "locName", type: ["nonEditableColumn"], width: 180, filterType: "string" },
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 280, filterType: "string" },
    { headerName: "invClassCd", field: "invClassCd", type: ["nonEditableColumn"], width: 120, filterType: "string", filterOption: INV_CLASS_CD_LIST,
      cellRenderer: AbcXyzChip },
    { headerName: "shipmentQtyPlan", field: "shipPlanQty", colId: "shipmentQtyPlan", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 150, filterType: "number" },
    { headerName: "shipmentQtyCfm", field: "shipActQty", colId: "shipmentQtyCfm", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 150, filterType: "number" },
    {
      headerName: "progress",
      field: "progress",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 150,
      filterType: 'percent',
      valueFormatter: params => params.value != null ? `${(params.value * 100).toFixed(1)}%` : "-",
      cellStyle: params => {
        const val = params.value != null ? params.value * 100 : null;
        if (val != null && thresholdRef.current.upper != null && val > thresholdRef.current.upper) {
          return { backgroundColor: "#ff4d4f", color: "#ffffff" };
        }
        if (val != null && thresholdRef.current.lower != null && val < thresholdRef.current.lower) {
          return { backgroundColor: "#ff4d4f", color: "#ffffff" };
        }
        return null;
      },
    },
    { headerName: "targetInventory", field: "targetInvQty", colId: "targetInventory", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 150, filterType: "number" },
    { headerName: "onhandQty", field: "invOnHand", colId: "onhandQty", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 150, filterType: "number" },
    { headerName: "inventoryCoverageDays", field: "invCoverageDays", colId: "inventoryCoverageDays", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 180, filterType: "number" },
    { headerName: "otdLtD", field: "otdLtDays", colId: "otdLtD", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 150, filterType: "number" },
  ];

  const defaultGridMemo = useMemo(() => DefaultGridSetting({ title, viewName }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (rpVersionFilter?.id) {
      searchFilters.versionId = rpVersionFilter.id;
    }

    if (filterValue?.locations) {
      searchFilters.destLocCds = filterValue?.locations?.map(location => location?.locCd).filter(Boolean);
    }

    if (filterValue?.category) {
      searchFilters.itemHrchyCds = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (invClassCdList?.length > 0) {
      searchFilters.invClassCdList = invClassCdList;
    }

    if (threshold.upper != null) {
      searchFilters.upperThreshold = threshold.upper / 100;
    }
    if (threshold.lower != null) {
      searchFilters.lowerThreshold = threshold.lower / 100;
    }

    if (isPagination) {
      params.page = currentPage ? currentPage - 1 : 0;
      params.pageSize = pageSize ? pageSize : 30;
    }

    searchFilters.nowDate = CURRENT_DATE;
    params.pagination = isPagination;
    params.searchFilters = searchFilters;

    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    return { ...params, ...sortParams };
  };

  // Y축 눈금 생성 함수: min, max, 눈금 개수 기반으로 적절한 간격의 눈금값 배열 반환
  const generateTicks = (min, max, tickCount = 5) => {
    if (typeof min !== 'number' || typeof max !== 'number') return undefined;
    const range = max - min;
    if (range <= 0) return [min];
    const rawStep = range / (tickCount - 1);
    const exp = Math.floor(Math.log10(rawStep));
    const magnitude = Math.pow(10, exp);
    const normalized = rawStep / magnitude;
    const niceStep = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    const step = niceStep * magnitude;
    const startIdx = Math.floor(min / step);
    const endIdx = Math.ceil(max / step);
    const ticks = [];
    for (let i = startIdx; i <= endIdx; i++) ticks.push(Math.round(i * step));
    if (!ticks.includes(0) && min <= 0 && max >= 0) { ticks.push(0); ticks.sort((a, b) => a - b); }
    return ticks;
  };

  const applyChartData = (cd) => {
    chartRawRef.current = cd ?? null;

    if (cd?.length > 0) {
      const labels = cd.map(p => p.baseDate);
      setChartLabel(labels);
      const invOnHandRow = { measure: t("onhandQty") };
      const shipActRow = { measure: t("shipmentQtyCfm") };
      const targetRow = { measure: t("targetInventory") };
      const shipPlanRow = { measure: t("shipmentQtyPlan") };
      const progressRow = { measure: t("progress") };
      cd.forEach(pt => {
        invOnHandRow[pt.baseDate] = pt.invOnHand ?? 0;
        shipActRow[pt.baseDate] = pt.shipActQty ?? 0;
        targetRow[pt.baseDate] = pt.targetInvQty ?? 0;
        shipPlanRow[pt.baseDate] = pt.shipPlanQty ?? 0;
        progressRow[pt.baseDate] = pt.progress == null ? '-' : pt.progress * 100;
      });

      const rawLeftMin = Math.min(0, ...cd.map(pt => pt.invOnHand ?? 0));
      if (rawLeftMin < 0) {
        const rawLeftMax = Math.max(
          1,
          ...cd.map(pt => Math.max(0, pt.invOnHand ?? 0) + (pt.shipActQty ?? 0) + (pt.targetInvQty ?? 0)),
          ...cd.map(pt => pt.shipPlanQty ?? 0)
        );
        const rawRightMax = Math.max(1, ...cd.map(pt => pt.progress != null ? pt.progress * 100 : 0));
        const pad = 0.1;
        const leftTicks = generateTicks(Math.floor(rawLeftMin * (1 + pad)), Math.ceil(rawLeftMax * (1 + pad)));
        const leftTickMin = leftTicks[0];
        const leftTickMax = leftTicks[leftTicks.length - 1];
        const rightTickMax = Math.ceil(rawRightMax * (1 + pad)); // 두 축의 0이 같은 수평선에 위치하도록 조정
        const rightTicks = generateTicks(
          Math.floor(-(rawRightMax * Math.abs(rawLeftMin) / rawLeftMax) * (1 + pad)),
          rightTickMax
        );
        const alignedRightMin = rightTicks[rightTicks.length - 1] * leftTickMin / leftTickMax;
        const rightTicksAligned = rightTicks.filter(t => t >= alignedRightMin);
        setChartYAxisMin(leftTickMin);
        setChartLeftYAxisMax(leftTickMax);
        setChartRightYAxisDomain([alignedRightMin, rightTicks[rightTicks.length - 1]]);
        setChartLeftYAxisTicks(leftTicks);
        setChartRightYAxisTicks(rightTicksAligned);
      } else {
        const rawLeftMax = Math.max(
          1,
          ...cd.map(pt => (pt.invOnHand ?? 0) + (pt.shipActQty ?? 0) + (pt.targetInvQty ?? 0)),
          ...cd.map(pt => pt.shipPlanQty ?? 0)
        );
        const rawRightMax = Math.max(1, ...cd.map(pt => pt.progress != null ? pt.progress * 100 : 0));
        const leftTicks = generateTicks(0, Math.ceil(rawLeftMax * 1.1));
        const rightTicks = generateTicks(0, Math.ceil(rawRightMax * 1.1));
        setChartYAxisMin(leftTicks[0]);
        setChartLeftYAxisMax(leftTicks[leftTicks.length - 1]);
        setChartRightYAxisDomain([rightTicks[0], rightTicks[rightTicks.length - 1]]);
        setChartLeftYAxisTicks(leftTicks);
        setChartRightYAxisTicks(rightTicks);
      }
      setChartData([invOnHandRow, shipActRow, targetRow, shipPlanRow, progressRow]);
    } else {
      setChartData([]);
      setChartLabel([]);
      setChartYAxisMin(0);
      setChartLeftYAxisMax("auto");
      setChartRightYAxisDomain([0, "auto"]);
      setChartLeftYAxisTicks(undefined);
      setChartRightYAxisTicks(undefined);
    }
  };

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    if (isEmpty(chartRawRef.current)) {
      return;
    }
    applyChartData(chartRawRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const retrieveChartData = (row) => {
    if (!row || !rpVersionFilter?.id) return;
    const chartSearchFilters = {
      versionId: rpVersionFilter.id,
      destLocCd: row.destLocCd,
      itemCd: row.itemCd,
      bucket: bucketType
    };
    shippingAlertService.getChart({ searchFilters: chartSearchFilters })
      .then(response => {
        applyChartData(response.data?.chartData);
      })
      .catch(e => debug(e));
  };

  const onBtShowLoading = useCallback(() => {
    gridRef.current?.api?.setGridOption('loading', true);
  }, [])

  const onBtHide = useCallback(() => {
    gridRef.current?.api?.setGridOption('loading', false);
  }, [])

  const retrieveShippingAlert = () => {
    if (!rpVersionFilter?.id) return;

    onBtShowLoading();
    const params = getRequestParams();
    shippingAlertService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems } = response.data;
        setRowData(results ?? []);
        setGridPages(totalPages ?? 0);
        resultTotalCount.current = totalItems ?? 0;

        if (!results?.length) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => debug(e))
      .finally(() => {
        onBtHide();
        const reduxState = reduxUtil.createObjectState(viewName, {
          rpVersion: rpVersionFilter,
          filter: filterValue,
          itemSearch: itemSearch.current,
          extraItemCodes: extraItemCodesRef.current,
          invClassCdList,
          advancedFilters,
          currentPage,
          pageSize,
        });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  };

  const retrieveThreshold = () => {
    p13nService.get(PAGE_CD)
      .then(response => {
        const configObj = response.data;
        if (configObj && (configObj.upperThreshold != null || configObj.lowerThreshold != null)) {
          const newThreshold = {
            upper: configObj.upperThreshold ?? 110,
            lower: configObj.lowerThreshold ?? null,
          };
          thresholdRef.current = newThreshold;
          setThreshold(newThreshold);
          reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { threshold: newThreshold })));
        }
      })
      .catch(() => { });
  };

  const handleAlignment = (_, newAlignment) => {
    if (!isNull(newAlignment)) {
      setBucketType(newAlignment);
    }
  };

  const saveThreshold = () => {
    const upper = thresholdInput.upper !== "" ? Number(thresholdInput.upper) : null;
    const lower = thresholdInput.lower !== "" ? Number(thresholdInput.lower) : null;
    p13nService.upsert({
      pageCd: PAGE_CD,
      configObj: { upperThreshold: upper, lowerThreshold: lower },
    })
      .then(() => {
        thresholdRef.current = { upper, lower };
        setThreshold({ upper, lower });
        reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { threshold: { upper, lower } })));
        gridRef.current?.api?.refreshCells({ force: true, columns: ["progress"] });
        setThresholdModalOpen(false);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      });
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveShippingAlert();
    }
  };

  const collapseFilter = () => setOpenFilter(prev => !prev);

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const handleThresholdOpen = () => {
    setThresholdInput({
      upper: thresholdRef.current.upper != null ? String(thresholdRef.current.upper) : "",
      lower: thresholdRef.current.lower != null ? String(thresholdRef.current.lower) : "",
    });
    setThresholdModalOpen(true);
  };

  useEffect(() => {
    retrieveThreshold();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      setAdvancedFilterColor(muicolor.INFO);
    } else {
      setAdvancedFilterColor(muicolor.DEFAULT);
    }
  }, [advancedFilters]);


  useEffect(() => {
    if (rpVersionFilter?.id) {
      retrieveShippingAlert();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortParams]);

  useEffect(() => {
    if (rpVersionFilter?.id && gridLoading) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter, filterValue, invClassCdList, advancedFilters, threshold]);

  useEffect(() => {
    setRowData([]);
    if (rpVersionFilter?.planConfigObj?.BUCKET_UNIT) {
      setBucketType(rpVersionFilter.planConfigObj.BUCKET_UNIT.toLowerCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter]);

  useEffect(() => {
    selectedRowRef.current = null;
    setSelectedRow(rowData?.length > 0 ? rowData[0] : null);
    if (rowData?.length > 0) {
      retrieveChartData(rowData[0]);
    } else {
      setChartData([]);
      setChartLabel([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData]);

  useEffect(() => {
    if (rowData?.length > 0) {
      retrieveChartData(selectedRowRef.current ?? rowData[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketType]);

  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[PLANNED, RELEASED]} useShipmentTxnOnly />
        <BucketToggleButton buckets={[buckets.DAY, buckets.WEEK, buckets.MONTH]} value={bucketType} onChange={handleAlignment} key={"BUCKET"} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(l => l?.locCd)} onChangeFilter={setFilterValue} />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <AbcXyzFilterModal invClassCdList={invClassCdList} setInvClassCdList={setInvClassCdList} />
        <Box>
          <SettingsButton label={t("alertSettings")} size="small" onClick={handleThresholdOpen} />
          <ExcelExportButton title={title} service={shippingAlertService} params={{ ...getRequestParams() }} columnDefs={columnDefs} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="grid-area">
        <Box sx={{ display: "flex", height: "100%" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SplitterContainer initialSizes={[6, 4]} collapseDirection="up">
              <Box id="rp-shipping-alert-chart" className="chart-grid-area chart-area" sx={{ height: "100%", mb: 10, display: "flex", flexDirection: "column" }}>
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <PivotChartComposed
                    pivotConfig={pivotConfig}
                    data={chartData}
                    options={{ tooltipContentStyle: { fontSize: FS.SUB }, yAxisDomain: [chartYAxisMin, chartLeftYAxisMax], yAxisDomainRight: chartRightYAxisDomain, yAxisTicks: chartLeftYAxisTicks, yAxisTicksRight: chartRightYAxisTicks, stackOffset: "sign" }}
                  />
                </Box>
              </Box>
              <Box sx={{ height: "100%", pt: 1, display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.5, minHeight: 36, backgroundColor: "#f9f9f9", borderBottom: "1px solid #e8e8e8", flexShrink: 0 }}>
                  {rpVersionFilter?.execTs && (
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {t("RpRunDate")}: {dateUtils.formatDate(rpVersionFilter.execTs, "yyyy-MM-dd")}
                    </Typography>
                  )}
                  {selectedRow && (
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {"\u00A0"}|{"\u00A0"}{t("lastInventoryDate")}: {selectedRow.lastInvTxnDate ?? "-"}
                      {"\u00A0\u00B7\u00A0"}{t("lastIntransitDate")}: {selectedRow.lastIntransitInvDate ?? "-"}
                      {"\u00A0\u00B7\u00A0"}{t("lastShipmentDate")}: {selectedRow.lastShipmentTxnDate ?? "-"}
                    </Typography>
                  )}
                  {thresholdLabel && (
                    <Typography variant="body2" sx={{ color: "#666", ml: "auto" }}>{t("alertSettings")}: {thresholdLabel}</Typography>
                  )}
                </Box>
                <Box id="rp-shipping-alert-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, mb: 1 }}>
                  <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    {...defaultGridMemo}
                    rowSelection={{ ...defaultGridMemo.rowSelection, mode: "singleRow" }}
                    onGridReady={onGridReady}
                    onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
                    onRowClicked={params => { selectedRowRef.current = params.data; setSelectedRow(params.data); retrieveChartData(params.data); }}
                  />
                </Box>
                <PaginationContainer
                  currentPage={currentPage} totalPages={gridPages}
                  pageSize={pageSize} pageSizes={pageSizes}
                  setCurrentPage={setCurrentPage} setPageSize={setPageSize}
                />
              </Box>
            </SplitterContainer>
          </Box>
          {openFilter && (
            <>
              <ResizableFilterPanel>
                <AdvancedFilter
                  columnDefs={columnDefs}
                  advancedFilters={advancedFilters}
                  onApply={setAdvancedFilters}
                  onClose={() => setOpenFilter(false)}
                />
              </ResizableFilterPanel>
            </>
          )}
        </Box>
      </Box>
      <BaseConfigModal
        open={thresholdModalOpen}
        onClose={() => setThresholdModalOpen(false)}
        title={t("alertSettings")}
        onSave={saveThreshold}
        size="md">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: 120, fontSize: FS.TITLE }}>{t("greaterThan")}</Typography>
            <TextField
              size="small"
              type="number"
              value={thresholdInput.upper}
              onChange={e => setThresholdInput(prev => ({ ...prev, upper: e.target.value }))}
              InputProps={{ endAdornment: <Typography sx={{ fontSize: FS.TITLE }}>%</Typography> }}
              sx={{ width: 140, "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 } }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: 120, fontSize: FS.TITLE }}>{t("lessThan")}</Typography>
            <TextField
              size="small"
              type="number"
              value={thresholdInput.lower}
              onChange={e => setThresholdInput(prev => ({ ...prev, lower: e.target.value }))}
              InputProps={{ endAdornment: <Typography sx={{ fontSize: FS.TITLE }}>%</Typography> }}
              sx={{ width: 140, "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 } }}
            />
          </Box>
        </Box>
      </BaseConfigModal>

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ShippingAlert);